import type { RaceProgressEntry, RoomMember, RoomPhase } from '@cube-rush/shared';
import { ru } from '../../i18n/ru';
import { cubeAccent } from '../../render/icons';
import { Avatar } from '../components/Avatar';
import { CubePreview } from '../components/CubePreview';
import { PeerVoiceBadge, PeerVolumeButton } from './PeerVoice';
import './MemberCard.css';

export interface MemberCardProps {
  member: RoomMember;
  phase: RoomPhase;
  isMe: boolean;
  isHost: boolean;
  speaking: boolean;
  /** The member's race progress while a race runs. */
  progress: RaceProgressEntry | undefined;
  /** Levels of the running race (a race by levels has 3 to 20, the room decides). */
  levelCount: number;
  /** A race against the clock: the badge says the level without a total («Уровень 3»). */
  timed?: boolean;
}

interface Badge {
  text: string;
  tone: 'ready' | 'wait' | 'offline' | 'race' | 'done' | 'watch';
}

/** What to say about a member right now (one main badge, the most important one). */
function memberBadge(
  member: RoomMember,
  phase: RoomPhase,
  progress: RaceProgressEntry | undefined,
  levelCount: number,
  timed: boolean,
): Badge {
  if (!member.connected) return { text: ru.room.offlineBadge, tone: 'offline' };
  if (phase === 'countdown' || phase === 'race') {
    // Before the role: the server makes whoever gives up a spectator of the rest of the race.
    if (progress?.quit) return { text: ru.room.quitBadge, tone: 'offline' };
    if (member.role !== 'racer' || !progress)
      return { text: ru.room.spectatorBadge, tone: 'watch' };
    if (progress.finishTm !== null) return { text: ru.room.finishedBadge, tone: 'done' };
    const level = Math.min(progress.lvl + 1, levelCount);
    const text = timed ? ru.room.levelBadgeOpen(level) : ru.room.levelBadge(level, levelCount);
    return { text, tone: 'race' };
  }
  return member.ready
    ? { text: ru.room.readyBadge, tone: 'ready' }
    : { text: ru.room.notReadyBadge, tone: 'wait' };
}

export function MemberCard({
  member,
  phase,
  isMe,
  isHost,
  speaking,
  progress,
  levelCount,
  timed = false,
}: MemberCardProps) {
  const { profile } = member;
  const badge = memberBadge(member, phase, progress, levelCount, timed);
  return (
    <li class={`member${isMe ? ' member--me' : ''}${member.connected ? '' : ' member--offline'}`}>
      {/* The microphone sits on the avatar's corner (as in voice chat apps): the name keeps the room. */}
      <span class="member__face">
        <Avatar
          avatar={profile.avatar}
          speaking={speaking}
          dimmed={!member.connected}
          ring={cubeAccent(profile.skin)}
        />
        <span
          class={`member__mic${member.muted ? ' member__mic--off' : ''}`}
          role="img"
          aria-label={member.muted ? ru.room.micOff : ru.room.micOn}
        >
          {member.muted ? '🔇' : '🎤'}
        </span>
      </span>
      <div class="member__main">
        <div class="member__name">
          {isHost && (
            <span class="member__crown" role="img" aria-label={ru.room.host}>
              👑
            </span>
          )}
          <span class="member__name-text">{profile.name}</span>
          {isMe && <span class="member__you">({ru.common.you})</span>}
        </div>
        <div class="member__badges">
          <span class={`badge badge--${badge.tone}`}>{badge.text}</span>
          {speaking && <span class="badge badge--speaking">{ru.room.speaking}</span>}
          {!isMe && <PeerVoiceBadge member={member} />}
        </div>
      </div>
      <CubePreview look={profile.skin} size={34} class="member__cube" />
      {!isMe && <PeerVolumeButton member={member} />}
    </li>
  );
}
